import { Auction } from "core/types";
import { Dispatch, SetStateAction } from "react";
import { useTranslations } from "translations";
import { DocsFormDataMapped, DocsFormName, FormFieldConfig } from "./Forms";
import { HeaderBanners, HeaderBannerType } from "./HeaderBanners";
import { SourceWrapper } from "./utils";

export function DocsFormFields<T extends DocsFormName>({
  auction,
  config,
  headerBanner,
  importData,
  onCloseHeaderBanner,
  setSourceSelected,
  skipRequiredValidation,
  sourceSelected,
}: {
  auction: Auction;
  config: FormFieldConfig<T>;
  setSourceSelected: Dispatch<
    SetStateAction<keyof DocsFormDataMapped<T> | undefined>
  >;
  sourceSelected: keyof DocsFormDataMapped<T> | undefined;
  skipRequiredValidation: boolean;
  headerBanner: HeaderBannerType | null;
  importData: () => void;
  onCloseHeaderBanner: () => void;
}) {
  const translations = useTranslations();

  if (!config.sections) return null;

  return (
    <div className="flex flex-col gap-4 p-4">
      <HeaderBanners
        banner={headerBanner}
        importData={importData}
        onClose={onCloseHeaderBanner}
      />
      <div className="flex w-full flex-col rounded-lg shadow-sm">
        <h2 className="w-full p-4 text-heading-lg font-bold">
          {config.title({ translations })}
        </h2>
        {config.sections
          .sort((a, b) => a.position - b.position)
          .map((section) => (
            <div key={section.position} className="flex w-full flex-col">
              <hr className="w-full border-gray-400" />
              <h3 className="w-full p-4 text-heading-lg font-bold">
                {section.title({ translations })}
              </h3>
              {section.description && (
                <p className="w-full p-4 text-body-md">
                  {section.description({ translations })}
                </p>
              )}
              <div className="flex flex-col items-center gap-2 p-4 pt-0">
                {(
                  Object.keys(section.fields) as Array<
                    keyof DocsFormDataMapped<T>
                  >
                )
                  .sort((a, b) => {
                    const aField = section.fields[a];
                    const bField = section.fields[b];
                    if (!aField || !bField) return 0;
                    return aField.position - bField.position;
                  })
                  .map((fieldName) => {
                    const field = section.fields[fieldName];
                    if (!field) return null;
                    return (
                      <div
                        key={String(fieldName)}
                        className="flex w-full flex-col gap-2"
                      >
                        {field.subTitle && (
                          <h4 className="w-full p-4 text-heading-lg font-bold">
                            {field.subTitle({ translations })}
                          </h4>
                        )}
                        {field.description && (
                          <p className="w-full p-4 text-body-md">
                            {field.description({ translations })}
                          </p>
                        )}
                        <SourceWrapper<T>
                          fieldName={fieldName}
                          hideSource={field.hideSource}
                          setSourceSelected={setSourceSelected}
                          sourceSelected={sourceSelected}
                        >
                          {field.input({
                            auction,
                            setSourceSelected,
                            skipRequiredValidation,
                            sourceSelected,
                            translations,
                          })}
                        </SourceWrapper>
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
